import {
  createContext,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import styled from '@emotion/styled'
import { Alert } from '@mui/material'
import {
  colors,
  Snackbar as BsSnackbar,
  typography,
} from '@resiliatech/birdsong'

type Severity = 'info' | 'success' | 'error' | 'warning' | undefined
type Variant = 'filled' | 'outlined' | 'standard'

type SnackbarProps = {
  anchorOrigin?: {
    vertical: 'top' | 'bottom'
    horizontal: 'left' | 'center' | 'right'
  }
  content: ReactNode
  title?: ReactNode
  icon?: ReactNode
  open: boolean
  onClose: () => void
  severity?: Severity
  variant?: Variant
}

type ToastOptions = { severity?: Severity; timeout?: number; variant?: Variant }

export const StyledAlert = styled(Alert)<{ severity?: Severity }>`
  background-color: ${({ severity }) => {
    switch (severity) {
      case 'info':
        return colors.feedback.infoLight
      case 'warning':
        return colors.feedback.errorLight
      case 'error':
        return colors.feedback.errorLight
      default:
        return colors.feedback.successLight
    }
  }};

  color: ${({ severity }) => {
    switch (severity) {
      case 'info':
        return colors.feedback.infoDark
      case 'warning':
        return colors.feedback.errorDark
      case 'error':
        return colors.feedback.errorDark
      default:
        return colors.feedback.successDark
    }
  }};

  ${typography.down1.regularSans};
`

export const Snackbar = ({
  anchorOrigin,
  content,
  title,
  icon,
  open,
  onClose,
  severity,
  variant,
}: SnackbarProps) => (
  <BsSnackbar
    ClickAwayListenerProps={{ onClickAway: () => null }}
    open={open}
    onClose={onClose}
    anchorOrigin={
      anchorOrigin || {
        vertical: 'bottom',
        horizontal: 'left',
      }
    }
  >
    <StyledAlert severity={severity} variant={variant || 'filled'} icon={icon}>
      {title && (
        <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
          {title}
        </div>
      )}
      {content}
    </StyledAlert>
  </BsSnackbar>
)

// eslint-disable-next-line @typescript-eslint/naming-convention
const SnackbarContext = createContext<{
  popToast: ({
    title,
    content,
    timeout,
  }: {
    title?: ReactNode
    content: ReactNode
    options?: { severity?: Severity; variant?: Variant }
    timeout?: number
  }) => void
}>({
  popToast: () => undefined,
})

const SnackbarProvider = ({ children }: { children: ReactElement }) => {
  const [severity, setSeverity] = useState<Severity>()
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false)
  const [snackbarTitle, setSnackbarTitle] = useState<ReactNode>('')
  const [snackbarContent, setSnackbarContent] = useState<ReactNode>('')
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null)

  const resetToastOptions = useCallback(() => {
    setIsSnackbarOpen(false)
    setSnackbarTitle('')
    setSnackbarContent('')
    setSeverity(undefined)
  }, [])

  const handleToast = useCallback(
    ({
      title,
      content,
      options = {},
    }: {
      title?: ReactNode
      content: ReactNode
      options?: ToastOptions
    }) => {
      // Clear existing timeout for any visible toasts
      if (timeoutId) {
        clearTimeout(timeoutId)
        resetToastOptions()
      }

      // Set styles and content for snackbar when called
      if (options.severity) setSeverity(options.severity)
      setSnackbarTitle(title || '') // Default to an empty string if no title is provided
      setSnackbarContent(content)
      setIsSnackbarOpen(true)

      // Close snackbar after desired timeout
      const newTimeoutId = setTimeout(() => {
        resetToastOptions()
      }, options.timeout || 6000)

      setTimeoutId(newTimeoutId)
    },
    [resetToastOptions, timeoutId]
  )

  const contextValue = useMemo(
    () => ({
      popToast: handleToast,
    }),
    [handleToast]
  )

  return (
    <SnackbarContext.Provider value={contextValue}>
      {timeoutId && (
        <Snackbar
          open={isSnackbarOpen}
          onClose={() => setIsSnackbarOpen(false)}
          title={snackbarTitle}
          content={snackbarContent}
          severity={severity}
        />
      )}
      {children}
    </SnackbarContext.Provider>
  )
}

export const useSnackbar = () => {
  const context = useContext(SnackbarContext)

  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }

  return context
}

export { SnackbarProvider }
